import { createFileRoute, Link } from "@tanstack/react-router";
import { PricingPlans } from "@/features/public-pricing/plans";
import { pricingCatalogOptions } from "@/features/public-pricing/query";
import faq from "@/features/public-pricing/faq.json";
import { seoHead, breadcrumbSchema, organizationSchema, websiteSchema } from "@/lib/seo";
import { JsonLd } from "@/components/seo/json-ld";

export const Route = createFileRoute("/_content/pricing/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pricingCatalogOptions()),
  head: () =>
    seoHead({
      title: "Pricing",
      path: "/pricing",
      description:
        "See Bavimail pricing for transactional email, inbound email, webhooks, retention, and production deliverability workflows.",
    }),
  component: PricingPage,
});
function PricingPage() {
  return (
    <main id="marketing-main" className="mx-auto max-w-7xl px-6 py-16 md:px-10">
      <JsonLd
        data={[
          organizationSchema(),
          websiteSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Pricing", path: "/pricing" },
          ]),
        ]}
      />
      <header className="mb-12 max-w-3xl">
        <p className="mb-4 text-sm uppercase tracking-widest text-hb-accent">Pricing</p>
        <h1 className="text-4xl font-extrabold md:text-6xl">Start free. Grow with your product.</h1>
        <p className="mt-6 text-lg text-hb-muted">
          Choose the plan that matches your sending stage. Compare current prices, included usage,
          and storage below.
        </p>
      </header>
      <PricingPlans />
      <p className="mt-6 text-sm text-hb-muted">
        Review your selected plan’s billing amount and usage terms at checkout. Manage your
        subscription and track usage in{" "}
        <Link
          to="/dashboard/settings"
          search={{ tab: "billing" }}
          preload="intent"
          className="text-hb-accent underline"
        >
          billing settings
        </Link>
        .
      </p>
      <section className="mt-16" id="faq">
        <h2 className="text-3xl font-bold">Pricing questions</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {faq.pricing.map((item, index) => (
            <section key={index} className="rounded-xl border border-hb-card p-6">
              <h3 className="font-bold">{item.question}</h3>
              <p className="mt-3 leading-7 text-hb-muted">{item.answer}</p>
            </section>
          ))}
        </div>
        <p className="mt-6">
          Need a larger commercial arrangement, dedicated infrastructure, or a broader support
          package?{" "}
          <a href="mailto:support@bavimail.com" className="text-hb-accent underline">
            Contact support
          </a>
          .
        </p>
      </section>
      <nav aria-label="Next steps" className="mt-16 flex flex-wrap gap-6 text-hb-accent">
        <Link to="/docs" preload="intent">
          Read the documentation
        </Link>
        <Link to="/feature" preload="intent">
          Explore features
        </Link>
        <Link to="/compare" preload="intent">
          Compare providers
        </Link>
      </nav>
    </main>
  );
}
